import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, getToken, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import SlaTimer from "../components/SlaTimer";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { fmtDateTime, fmtTime, fmtDuration } from "../lib/format";
import {
  ArrowLeft, Send, Lock, Paperclip, Clock, Flag, RotateCcw, Loader2, Users,
  MessageSquare, ListChecks, History, Download, X,
} from "lucide-react";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin", "technician"].includes(user?.role);
  const canAssign = ["admin", "super_admin"].includes(user?.role);

  const LIVE = { refetchInterval: 5000, refetchIntervalInBackground: false, refetchOnWindowFocus: true };
  const ticketQ = useQuery({ queryKey: ["ticket", id], queryFn: () => api.get(`/tickets/${id}`).then((r) => r.data), ...LIVE });
  const messagesQ = useQuery({ queryKey: ["messages", id], queryFn: () => api.get(`/tickets/${id}/messages`).then((r) => r.data), ...LIVE });
  const historyQ = useQuery({ queryKey: ["history", id], queryFn: () => api.get(`/tickets/${id}/history`).then((r) => r.data), ...LIVE });
  const attachQ = useQuery({ queryKey: ["attach", id], queryFn: () => api.get(`/tickets/${id}/attachments`).then((r) => r.data), refetchOnWindowFocus: true });
  const techniciansQ = useQuery({
    queryKey: ["technicians"],
    queryFn: () => api.get("/admin/technicians").then((r) => r.data),
    enabled: canAssign,
  });
  const statusesQ = useQuery({ queryKey: ["ticket-statuses"], queryFn: () => api.get("/ticket-statuses").then((r) => r.data) });

  const t = ticketQ.data;
  const canEditAssignment = t?.status === "OPEN";

  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.post(`/tickets/${id}/read`).catch(() => {});
  }, [id]);

  const [prog, setProg] = useState(null);
  const [statusSel, setStatusSel] = useState(null);
  const [prioSel, setPrioSel] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [assignment, setAssignment] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (t?.technicians) setAssignment(t.technicians.map((technician) => String(technician.id)));
  }, [t?.technicians]);

  const threadRef = useRef(null);
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messagesQ.data]);

  if (ticketQ.isLoading) return <Layout><div className="p-10 text-center text-muted-foreground">Loading ticket…</div></Layout>;
  if (ticketQ.isError || !t) return <Layout><div className="p-10 text-center text-muted-foreground">Ticket not found or access denied.</div></Layout>;

  const progress = prog ?? t.progress;
  const status = statusSel ?? t.status;
  const priority = prioSel ?? t.priority;
  const statusLabel = (code) => statusesQ.data?.find((item) => item.code === code)?.label || code;

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message: reply, is_internal: internal });
      setReply("");
      setInternal(false);
      qc.invalidateQueries({ queryKey: ["messages", id] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      toast.success(internal ? "Internal note added" : "Reply sent");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await api.post(`/tickets/${id}/progress`, { progress, status, note: note || undefined });
      if (priority !== t.priority) await api.post(`/tickets/${id}/priority`, { priority });
      setNote("");
      setProg(null); setStatusSel(null); setPrioSel(null);
      qc.invalidateQueries();
      toast.success("Ticket updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/tickets/${id}/reopen`);
      qc.invalidateQueries();
      toast.success("Ticket reopened");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const saveAssignment = async () => {
    if (!canEditAssignment) return;
    setAssigning(true);
    try {
      await api.post(`/admin/tickets/${id}/assign`, { technician_ids: assignment.map(Number) });
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      toast.success("Technician assignment updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  const downloadUrl = (attId) => `${API}/attachments/${attId}?token=${getToken()}`;

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="rounded-lg border border-border bg-card p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-plex text-sm text-muted-foreground">{t.ticket_number}</span>
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} statusMeta={t.status_meta} />
            </div>
            <h1 className="font-heading font-black tracking-tighter text-3xl">{t.subject}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t.customer_name} · {t.customer_email}
            </p>
            {(t.customer_phone || t.site_location) && (
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                {t.customer_phone && <span><span className="font-medium text-foreground">Phone:</span> {t.customer_phone}</span>}
                {t.site_location && <span><span className="font-medium text-foreground">Site:</span> {t.site_location}</span>}
              </div>
            )}
          </div>
          {isAdmin && t.status === "DONE" && (
            <Button variant="outline" onClick={reopen} data-testid="reopen-btn" className="gap-2">
              <RotateCcw className="h-4 w-4" /> Reopen
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border text-sm">
          <TimeStat label="Submitted" value={fmtDateTime(t.created_at)} icon={Clock} />
          <TimeStat label="Work Started" value={fmtDateTime(t.started_at)} icon={Flag} />
          <TimeStat label="Resolved" value={fmtDateTime(t.resolved_at)} icon={ListChecks} />
          <TimeStat label="Done" value={fmtDateTime(t.done_at)} icon={ListChecks} />
        </div>
        {t.sla.resolution_seconds != null && (
          <div className="mt-4 text-sm">
            <span className="text-muted-foreground">Total resolution time: </span>
            <span className="font-semibold font-mono-plex">{fmtDuration(t.sla.resolution_seconds)}</span>
            <span className="text-muted-foreground"> (started → done)</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: conversation + timeline */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="conversation">
            <TabsList data-testid="detail-tabs">
              <TabsTrigger value="conversation" data-testid="tab-conversation"><MessageSquare className="h-4 w-4 mr-1.5" />Conversation</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline"><History className="h-4 w-4 mr-1.5" />Activity Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="conversation" className="mt-4">
              <div className="rounded-lg border border-border bg-card">
                <div ref={threadRef} className="p-5 space-y-4 max-h-[460px] overflow-y-auto" data-testid="conversation-thread">
                  {messagesQ.data?.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No messages yet.</p>}
                  {messagesQ.data?.map((m) => (
                    <div key={m.id} className={`flex ${["admin", "super_admin", "technician"].includes(m.role) ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        m.is_internal
                          ? "bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800"
                          : ["admin", "super_admin", "technician"].includes(m.role)
                          ? "bg-secondary"
                          : "bg-primary text-primary-foreground"
                      }`} data-testid={`message-${m.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold">{m.user_name}</span>
                          <span className={`text-[10px] uppercase tracking-wide ${m.role === "user" && !m.is_internal ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.role}</span>
                          {m.is_internal && <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 dark:text-amber-400 font-semibold"><Lock className="h-2.5 w-2.5" />Internal</span>}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                        <div className={`text-[10px] mt-1 ${m.role === "user" && !m.is_internal ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{fmtDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border p-4 space-y-3">
                  <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={internal ? "Internal note (hidden from customer)…" : "Write a reply…"} data-testid="reply-input" />
                  <div className="flex items-center justify-between">
                    {isAdmin ? (
                      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-toggle" className="accent-amber-500" />
                        <Lock className="h-3.5 w-3.5" /> Internal note
                      </label>
                    ) : <span />}
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="send-reply-btn" className="gap-2">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <div className="rounded-lg border border-border bg-card p-5" data-testid="activity-timeline">
                <div className="relative pl-6 space-y-6">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
                  {historyQ.data?.progress_logs?.map((log) => (
                    <div key={log.id} className="relative">
                      <div className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full bg-primary ring-4 ring-card" />
                      <div className="text-xs text-muted-foreground font-mono-plex">{fmtDateTime(log.created_at)}</div>
                      <div className="text-sm font-medium mt-0.5">
                        {log.old_status == null
                          ? "Ticket created"
                          : log.new_status !== log.old_status
                          ? `Status: ${statusLabel(log.old_status)} → ${statusLabel(log.new_status)}`
                          : `Progress updated`}
                      </div>
                      {(log.new_progress !== log.old_progress) && (
                        <div className="text-sm text-muted-foreground">Progress {log.old_progress ?? 0}% → {log.new_progress}%</div>
                      )}
                      {log.note && log.note !== "Demo progress update" && <div className="text-sm text-muted-foreground italic mt-0.5">"{log.note}"</div>}
                      {log.stage_seconds != null && <div className="text-[11px] text-muted-foreground mt-0.5">Stage took {fmtDuration(log.stage_seconds)} · by {log.user_name}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Description + attachments */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Description</div>
            <p className="text-sm whitespace-pre-wrap">{t.description}</p>
            {attachQ.data?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Attachments</div>
                <div className="flex flex-wrap gap-2">
                  {attachQ.data.map((a) => (
                    <a key={a.id} href={downloadUrl(a.id)} target="_blank" rel="noreferrer" data-testid={`attachment-${a.id}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors">
                      <Paperclip className="h-3.5 w-3.5" /> {a.original_name} <Download className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: SLA + progress + admin controls */}
        <div className="space-y-6">
          <SlaTimer sla={t.sla} priority={t.priority} />

          <div className="rounded-lg border border-border bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">Progress</div>
            <Progress value={t.progress} className="h-3" data-testid="progress-bar" />
            <div className="text-right text-sm font-semibold mt-1 font-mono-plex">{t.progress}%</div>
          </div>

          {isAdmin && (
            <div className="rounded-lg border border-primary/30 bg-card p-5 space-y-4" data-testid="admin-controls">
              <div className="font-heading font-bold tracking-tight text-sm">Admin Controls</div>

              {canAssign && (
                <div className="space-y-2 pb-4 border-b border-border">
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Assign technicians</label>
                  {assignment.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {assignment.map((technicianId) => {
                        const technician = techniciansQ.data?.find((item) => String(item.id) === technicianId);
                        return (
                          <span key={technicianId} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs">
                            {technician?.name || "Unknown technician"}
                            <button type="button" onClick={() => setAssignment((current) => current.filter((id) => id !== technicianId))} disabled={!canEditAssignment} className="text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40" aria-label={`Remove ${technician?.name || "technician"}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <select
                    multiple
                    value={assignment}
                    onChange={(e) => setAssignment(Array.from(e.target.selectedOptions, (option) => option.value))}
                    disabled={!canEditAssignment}
                    className="flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="technician-select"
                  >
                    {techniciansQ.data?.map((technician) => <option key={technician.id} value={technician.id}>{technician.name} ({technician.email})</option>)}
                  </select>
                  <button type="button" onClick={() => setAssignment([])} disabled={!canEditAssignment || assignment.length === 0} className="text-xs text-destructive hover:underline disabled:opacity-50">Clear all assignments</button>
                  <Button onClick={saveAssignment} disabled={!canEditAssignment || assigning || techniciansQ.isLoading} variant="outline" className="w-full gap-2" data-testid="save-assignment-btn">
                    {assigning && <Loader2 className="h-4 w-4 animate-spin" />} Save Assignment
                  </Button>
                  {!canEditAssignment && <p className="text-[11px] text-muted-foreground">Assignment can only be changed while the ticket is OPEN.</p>}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={status} onValueChange={setStatusSel}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusesQ.data?.map((item) => <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Priority</label>
                <Select value={priority} onValueChange={setPrioSel}>
                  <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground flex justify-between"><span>Progress</span><span className="font-mono-plex font-semibold">{progress}%</span></label>
                <Slider value={[progress]} min={0} max={100} step={5} onValueChange={(v) => setProg(v[0])} data-testid="progress-slider" />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Note (optional)</label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for this update…" data-testid="update-note" />
              </div>

              <Button onClick={saveProgress} disabled={saving} className="w-full gap-2" data-testid="save-update-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Update
              </Button>
              <p className="text-[11px] text-muted-foreground">Every update is recorded permanently in the activity timeline.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function TimeStat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1"><Icon className="h-3 w-3" />{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
